/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import { createPermission } from '@backstage/plugin-permission-common';

/**
 * This permission is to control visibility of UI for the Online team.
 */
export const OnlineVisibilityPermission = createPermission({
  name: 'online.web.read',
  attributes: {
    action: 'read',
  }
});

/**
 * Reading Claude usage/spend metrics (see .claude/backlog/claude-metrics).
 *
 * Lives here, not in packages/backend, because the frontend panel and the backend
 * policy must agree on the NAME -- declaring it twice would let them drift silently
 * and drift here means either a dead gate or an exposed one.
 *
 * ADMIN-ONLY. The audience is the dev-productivity group, which is already part of the
 * policy's ADMIN_GROUPS, so the tier-3 branch grants it and everyone else is denied.
 *
 * NOTE on `attributes.action`: 'read' is honest, and it is kept even though the
 * three-tier policy grants ANY authenticated user ANY permission carrying
 * `action: 'read'`. So permissionPolicyExtension.ts has an EXPLICIT clause for this
 * permission ahead of that blanket branch. The alternative -- omitting the attribute so it
 * falls through -- would be a booby trap: the next person to "tidy up" by adding the
 * accurate attribute would silently expose per-person spend to every signed-in user, and
 * no test would fail.
 */
export const ClaudeMetricsReadPermission = createPermission({
  name: 'claude.metrics.read',
  attributes: {
    action: 'read',
  }
});

/**
 * Calling an MCP write tool, i.e. any action not flagged `readOnly` (PLAT-3071).
 *
 * The developer tier and up: the policy grants it with the tier-2 bundle, and its
 * `update` action keeps it out of the tier-1 blanket read grant. The actions-registry
 * guard checks it on every user call to a write tool, so read-only callers keep
 * every read tool and lose every write.
 */
export const McpWritePermission = createPermission({
  name: 'm42.mcp.write',
  attributes: {
    action: 'update',
  },
});

/**
 * Cancelling or retrying another person's scaffolder run over MCP (PLAT-3071).
 *
 * ADMIN-ONLY: it is in no tier-2 bundle and its `update` action misses the tier-1
 * read grant, so only the tier-3 branch allows it. Developers act on their own runs.
 */
export const ScaffolderAnyRunPermission = createPermission({
  name: 'm42.scaffolder.any-run',
  attributes: {
    action: 'update',
  },
});

/**
 * List of all catalog permissions.
 */
export const OCPCustomPermissions = [
  OnlineVisibilityPermission,
  ClaudeMetricsReadPermission,
  McpWritePermission,
  ScaffolderAnyRunPermission,
];